import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'
import RichTable from '../components/richTable/RichTable.jsx'
import tableHelpers, { decimalFormatter, intFormatter } from '../helpers/TableHelpers.js'
import PlanScorecard from './PlanScorecard.js'
import Tooltip from '../components/Tooltip.js';
import CircularProgress from '@mui/material/CircularProgress';
import ActionResult from '../components/ActionResult.js';
import TwoStepConfirmationButton from "../components/TwoStepConfirmButton.js"
import Dropdown from 'react-bootstrap/Dropdown';
import SaveIcon from '@mui/icons-material/Save';
import Drawer from '@mui/material/Drawer';
import AlgoStats from '../algos/AlgoStats.js';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AlgoDrawer from '../algos/AlgoDrawer.js'

function Algos(props) {
    const [data, setData] = useState([]);
    const [backtestResults, setBacktestResults] = useState(null);
    const [lastBacktestResults, setLastBacktestResults] = useState(null)
    const [isTestRunning, setIsTestRunning] = useState(false);
    const [focusAlgoId, setFocusAlgoId] = useState(null);
    const [result, setResult] = useState(null);
    const [statYears, setStatYears] = useState(5);
    const [updates, setUpdates] = useState(0);
    const [unsavedChanges, setUnsavedChanges] = useState(0);
    const [hideAlgos, setHideAlgos] = useState(true)
    const [batchUpdateCriteria, setBatchUpdateCriteria] = useState({
        trade_symbol: 'All',
        algo_type: 'All',
        entry_speed: 'All'
    });
    const [batchUpdateValue, setBatchUpdateValue] = useState('');
    const [algoCorrelations, setAlgoCorrelations] = useState([]);
    const [autoAllocatePreference, setAutoAllocatePreference] = useState(50);
    const [totalAutoAllocation, setTotalAutoAllocation] = useState(100);
    const [minAutoAllocation, setMinAutoAllocation] = useState(1);
    const [maxAutoAllocation, setMaxAutoAllocation] = useState(10);
    const [autoAllocateLongShort, setAutoAllocateLongShort] = useState('Both');

    useEffect(() => {
        (async function () {
            let text = await (await fetch(`/api/algos?planId=` + props.planId + `&years=` + statYears)).json();
            console.log(text);
            setData(text);

            let correlationsText = await (await fetch(`/api/planAlgoCorrelations?planId=` + props.planId)).json();
            console.log(correlationsText);
            let maxCorrelations = {};
            text.forEach(algo => {
                text.forEach(algo2 => {
                    if (algo.idalgos !== algo2.idalgos && algo.symbol === algo2.symbol && algo.algo_type === algo2.algo_type) {
                        let correlation = correlationsText.find(correlation => correlation.primary_algoId === algo.idalgos && correlation.secondary_algoId === algo2.idalgos);
                        let secondaryCorrelation = correlationsText.find(correlation => correlation.secondary_algoId === algo.idalgos && correlation.primary_algoId === algo2.idalgos);
                        if (correlation && secondaryCorrelation) {
                            if (!maxCorrelations[algo.idalgos]) {
                                maxCorrelations[algo.idalgos] = {
                                    algoId: algo2.idalgos,
                                    display_name: algo2.display_name,
                                    sum: correlation.percent + secondaryCorrelation.percent
                                };
                            } else if (maxCorrelations[algo.idalgos].sum < correlation.percent + secondaryCorrelation.percent) {
                                maxCorrelations[algo.idalgos] = {
                                    algoId: algo2.idalgos,
                                    display_name: algo2.display_name,
                                    sum: correlation.percent + secondaryCorrelation.percent
                                }
                            }
                        }
                    }
                })
            });
            console.log("maxCorrelations", maxCorrelations);
            setAlgoCorrelations(maxCorrelations);
        })();
    }, [props.planId, props.itemsChanged, statYears, updates]);

    async function handleSoftRemove(algoId) {
        let tempData = JSON.parse(JSON.stringify(data));
        tempData = tempData.filter(algo =>
            algo.algoId !== algoId && algo.idalgos !== algoId
        );
        console.log(tempData);
        setData(tempData);
        setUnsavedChanges(unsavedChanges + 1);
    }

    async function handleSwap(algoId) {
        let algo = data.find(algo => algo.idalgos === algoId || algo.algoId === algoId);
        if (!algo) return;

        let text = await (await fetch(`/api/algosByName?name=` + encodeURIComponent(algo.name) + `&symbol=` + encodeURIComponent(algo.symbol))).json();
        console.log("algosByName", text);
        if (text && text.length === 2) {
            let otherAlgo = text.find(a => (a.idalgos || a.algoId) !== (algo.idalgos || algoId));
            if (!otherAlgo) return;

            let stats = null;
            let algoInfo = otherAlgo;
            try {
                let algoDetails = await (await fetch(`/api/algo?algoId=` + otherAlgo.idalgos)).json();
                if (algoDetails && algoDetails.algo && algoDetails.algo.length) {
                    algoInfo = algoDetails.algo[0];
                }
                if (algoDetails && algoDetails.stats) {
                    stats = algoDetails.stats.find(s => s.years === statYears);
                }
            } catch (e) {
                console.error("Could not fetch algo stats for swap", e);
            }

            let swappedAlgo = {
                ...otherAlgo,
                ...algoInfo,
                idalgos: otherAlgo.idalgos,
                algoId: otherAlgo.idalgos,
                display_name: algoInfo.display_name,
                name: algoInfo.name,
                symbol: algoInfo.symbol,
                short: algoInfo.short,
                trade_symbol: algoInfo.trade_symbol,
                percentage: algo.percentage !== undefined ? algo.percentage : 0,
                algo_type: algoInfo.algo_type,
                entry_speed: algoInfo.entry_speed,
                entry_lookback: algoInfo.entry_lookback,
                exit_lookback: algoInfo.exit_lookback,
                count: stats ? stats.count : 0,
                avg_duration: stats ? stats.avg_duration : 0,
                winning_percentage: stats ? stats.winning_percentage : 0,
                max_drawdown: stats ? stats.max_drawdown : 0,
                cagr: stats ? stats.cagr : 0,
                net_max_drawdown: 0,
                avg_profit: stats ? stats.avg_profit : 0,
                avg_win: stats ? stats.avg_win : 0,
                avg_loss: stats ? stats.avg_loss : 0,
                max_win: stats ? stats.max_win : 0,
                max_loss: stats ? stats.max_loss : 0,
                robustness_score: algoInfo.robustness_score || algoInfo.robustness_overall || 0,
                live_vs_backtest: algoInfo.live_vs_backtest || 0
            };

            let tempData = JSON.parse(JSON.stringify(data));
            let index = tempData.findIndex(a => a.idalgos === algoId || a.algoId === algoId);
            if (index !== -1) {
                tempData.splice(index, 1, swappedAlgo);
            } else {
                tempData = tempData.filter(a => a.idalgos !== algoId && a.algoId !== algoId);
                tempData.push(swappedAlgo);
            }
            console.log("swapped tempData", tempData);
            setData(tempData);
            setUnsavedChanges(unsavedChanges + 1);
        }
    }

    async function updateAllocation(algoId, diplayName, newPercentage) {
        console.log("handleSubmitAllocationChange", algoId, newPercentage)
        const algo = data.find(algo => algo.idalgos === algoId);
        if (algo && props.allowEdit()) {
            //const url = `/api/updatePlanAlgoAllocation?planId=` + props.planId + `&algoId=` + algoId + `&percentage=` + newPercentage;
            //await (await fetch(url, { method: 'POST' })).json();
            //setResult({ success: diplayName + " updated to " + newPercentage });
        }
    }

    async function updateAllAllocations() {
        console.log("updateAllAllocations")

        let allocations = {};
        console.log("data", data);
        data.forEach(allocation => {
            if (allocation.percentage && Number(allocation.percentage)) {
                allocations[Number(allocation.idalgos)] = Number(allocation.percentage);
            } else {
                console.log("skipping", allocation);
            }
        })
        console.log("allocations", allocations);

        const url = `/api/updatePlanAllocations?planId=` + props.planId;
        let result = await (await fetch(url, { method: 'POST', body: JSON.stringify(allocations) })).json();
        console.log(result);
        setResult(result);
        setUpdates(updates + 1);
        setUnsavedChanges(0);
        props.onAllocationUpdated();
    }

    function onAllocationUpdated(algoId, event) {
        console.log("onAllocationUpdated", algoId, Number(event.target.value))
        const newData = data.map((algo) => {
            if (algo.idalgos === algoId) {
                return { ...algo, percentage: Number(event.target.value) };
            }
            return algo;
        });

        setData(newData);
    }

    function onAllocationUpdatedSet(algoId, newValue) {
        console.log("onAllocationUpdatedSet", algoId, newValue)
        const newData = data.map((algo) => {
            if (algo.idalgos === algoId) {
                const newPercentage = newValue;
                if (newPercentage >= 0 && newPercentage <= 100) {
                    updateAllocation(algoId, algo.display_name, newPercentage);
                    setUnsavedChanges(unsavedChanges + 1);
                    return { ...algo, percentage: newPercentage };
                } else {
                    return algo;
                }
            }
            return algo;
        });

        setData(newData);
    }

    function onAllocationIncremented(algoId, value) {
        const newData = data.map((algo) => {
            if (algo.idalgos === algoId) {
                const newPercentage = algo.percentage + value;
                if (newPercentage >= 0 && newPercentage <= 100) {
                    updateAllocation(algoId, algo.display_name, newPercentage);
                    setUnsavedChanges(unsavedChanges + 1);
                    return { ...algo, percentage: newPercentage };
                } else {
                    return algo;
                }
            }
            return algo;
        });

        setData(newData);
        console.log("onAllocationIncremented", algoId, value)
    }

    function handleBatchUpdate() {
        const newPercentage = Number(batchUpdateValue);
        if (isNaN(newPercentage) || newPercentage < 0 || newPercentage > 100) {
            setResult({ error: "Invalid percentage for batch update. Must be between 0 and 100." });
            return;
        }

        let changesCount = 0;
        const newData = data.map(algo => {
            const matchTradeSymbol = batchUpdateCriteria.trade_symbol === 'All' || algo.trade_symbol === batchUpdateCriteria.trade_symbol;
            const matchAlgoType = batchUpdateCriteria.algo_type === 'All' || algo.algo_type === batchUpdateCriteria.algo_type;
            const matchEntrySpeed = batchUpdateCriteria.entry_speed === 'All' || algo.entry_speed === batchUpdateCriteria.entry_speed;

            if (matchTradeSymbol && matchAlgoType && matchEntrySpeed) {
                if (algo.percentage !== newPercentage) {
                    changesCount++;
                }
                return { ...algo, percentage: newPercentage };
            }
            return algo;
        });

        if (changesCount > 0) {
            setData(newData);
            setUnsavedChanges(unsavedChanges + changesCount);
        }
        setBatchUpdateValue(''); // Reset input
    }

    function handleAutoAllocate() {
        if (data.length === 0) return;

        let algosToAllocate = data;
        if (autoAllocateLongShort === 'Long') {
            algosToAllocate = data.filter(a => !a.short);
        } else if (autoAllocateLongShort === 'Short') {
            algosToAllocate = data.filter(a => a.short);
        }

        if (algosToAllocate.length === 0) {
            setResult({ error: "No algos of the selected type to allocate." });
            return;
        }

        const targetTotal = Number(totalAutoAllocation);
        const minAlloc = Number(minAutoAllocation);
        const maxAlloc = Number(maxAutoAllocation);

        if (isNaN(targetTotal) || targetTotal <= 0) {
            setResult({ error: "Invalid total allocation." });
            return;
        }
        if (minAlloc > maxAlloc) {
            setResult({ error: "Min allocation cannot be greater than Max allocation." });
            return;
        }
        if (minAlloc * algosToAllocate.length > targetTotal) {
            setResult({ error: "Min allocation * number of algos exceeds total allocation." });
            return;
        }
        if (maxAlloc * algosToAllocate.length < targetTotal) {
            setResult({ error: "Max allocation * number of algos is less than total allocation." });
            return;
        }

        const weightCagr = autoAllocatePreference / 100;
        const weightDd = 1 - weightCagr;

        let maxCagr = -999999, minCagr = 999999, maxDd = -999999, minDd = 999999;

        algosToAllocate.forEach(algo => {
            const cagr = algo.cagr || 0;
            const dd = algo.max_drawdown || 0;
            if (cagr > maxCagr) maxCagr = cagr;
            if (cagr < minCagr) minCagr = cagr;
            if (dd > maxDd) maxDd = dd;
            if (dd < minDd) minDd = dd;
        });

        const cagrRange = maxCagr - minCagr === 0 ? 1 : maxCagr - minCagr;
        const ddRange = maxDd - minDd === 0 ? 1 : maxDd - minDd;

        const scoredAlgos = algosToAllocate.map(algo => {
            const normCagr = ((algo.cagr || 0) - minCagr) / cagrRange;
            const normDd = 1 - (((algo.max_drawdown || 0) - minDd) / ddRange);
            let score = Math.max((normCagr * weightCagr) + (normDd * weightDd), 0.01);
            return { ...algo, tempScore: score };
        });

        let remainingAlgos = scoredAlgos.map(a => ({ ...a }));
        let finalAllocations = {};
        let remainingTotal = targetTotal;

        let constrainMinMax = true;
        while (constrainMinMax && remainingAlgos.length > 0) {
            constrainMinMax = false;
            let currentTotalScore = remainingAlgos.reduce((sum, a) => sum + a.tempScore, 0);

            for (let i = 0; i < remainingAlgos.length; i++) {
                let algo = remainingAlgos[i];
                let calcPercentage = (algo.tempScore / currentTotalScore) * remainingTotal;

                if (calcPercentage > maxAlloc) {
                    finalAllocations[algo.idalgos] = maxAlloc;
                    remainingTotal -= maxAlloc;
                    remainingAlgos.splice(i, 1);
                    constrainMinMax = true;
                    break;
                } else if (calcPercentage < minAlloc) {
                    finalAllocations[algo.idalgos] = minAlloc;
                    remainingTotal -= minAlloc;
                    remainingAlgos.splice(i, 1);
                    constrainMinMax = true;
                    break;
                }
            }
        }

        let currentTotalScore = remainingAlgos.reduce((sum, a) => sum + a.tempScore, 0);
        remainingAlgos.forEach(algo => {
            finalAllocations[algo.idalgos] = (algo.tempScore / currentTotalScore) * remainingTotal;
        });

        let currentTotal = 0;
        let newData = algosToAllocate.map(algo => {
            let p = finalAllocations[algo.idalgos] !== undefined ? finalAllocations[algo.idalgos] : 0;
            let rounded = Math.round(p);
            if (rounded > maxAlloc) rounded = maxAlloc;
            if (rounded < minAlloc) rounded = minAlloc;
            currentTotal += rounded;
            const { tempScore, ...cleanAlgo } = scoredAlgos.find(sa => sa.idalgos === algo.idalgos);
            return { ...cleanAlgo, percentage: rounded };
        });

        let diff = targetTotal - currentTotal;

        let sortedByScore = [...newData].sort((a, b) => {
            let scoreA = scoredAlgos.find(sa => sa.idalgos === a.idalgos).tempScore;
            let scoreB = scoredAlgos.find(sa => sa.idalgos === b.idalgos).tempScore;
            return scoreB - scoreA;
        });

        while (diff > 0) {
            let adjusted = false;
            for (let i = 0; i < sortedByScore.length && diff > 0; i++) {
                if (sortedByScore[i].percentage < maxAlloc) {
                    sortedByScore[i].percentage += 1;
                    diff -= 1;
                    adjusted = true;
                    break;
                }
            }
            if (!adjusted) break;
        }

        while (diff < 0) {
            let adjusted = false;
            for (let i = sortedByScore.length - 1; i >= 0 && diff < 0; i--) {
                if (sortedByScore[i].percentage > minAlloc) {
                    sortedByScore[i].percentage -= 1;
                    diff += 1;
                    adjusted = true;
                    break;
                }
            }
            if (!adjusted) break;
        }

        let changesCount = 0;
        const finalData = data.map(originalAlgo => {
            const newAlgo = sortedByScore.find(a => a.idalgos === originalAlgo.idalgos);
            if (newAlgo) {
                if (originalAlgo.percentage !== newAlgo.percentage) changesCount++;
                return newAlgo;
            }
            return originalAlgo;
        });

        if (changesCount > 0) {
            setData(finalData);
            setUnsavedChanges(unsavedChanges + changesCount);
            setResult({ success: `Auto-allocated ${targetTotal}% across ${algosToAllocate.length} algos.` });
        }
    }

    function handleFileUpload(event) {
        const target = event.target;
        const file = target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const lines = content.split('\n');
            if (lines.length > 0) {
                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
                let idIdx = headers.findIndex(h => h === 'algoid' || h === 'algo id' || h === 'algo_id' || h === 'id');
                let allocIdx = headers.findIndex(h => h === 'allocation' || h === 'percentage' || h === 'alloc' || h === 'percent');

                if (idIdx === -1 || allocIdx === -1) {
                    setResult({ error: "CSV must contain algoId and allocation columns." });
                    target.value = null;
                    return;
                }

                const importedAllocations = {};
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue;
                    const cols = line.split(',').map(c => c.trim().replace(/"/g, ''));
                    if (cols.length > Math.max(idIdx, allocIdx)) {
                        const id = parseInt(cols[idIdx]);
                        const alloc = parseFloat(cols[allocIdx]);
                        if (!isNaN(id) && !isNaN(alloc)) {
                            importedAllocations[id] = alloc;
                        }
                    }
                }

                let changesCount = 0;
                const newData = data.map(algo => {
                    const newAlloc = importedAllocations[algo.idalgos] !== undefined ? importedAllocations[algo.idalgos] : 0;
                    if (algo.percentage !== newAlloc) {
                        changesCount++;
                    }
                    return { ...algo, percentage: newAlloc };
                });

                if (changesCount > 0) {
                    setData(newData);
                    setUnsavedChanges(unsavedChanges + changesCount);
                }
                setResult({ success: `Imported allocations. ${changesCount} changes made.` });
            }
            target.value = null;
        };
        reader.readAsText(file);
    }

    function getAlgos() {
        return data.map(a => {
            let maxCorrelation = algoCorrelations[a.idalgos]
            const winLossRatio = a.avg_loss === 0 ? a.avg_win : Math.abs(a.avg_win / a.avg_loss);

            return {
                id: a.idalgos,
                display_name: a.display_name,
                name: a.name,
                symbol: a.symbol,
                short: a.short,
                trade_symbol: a.trade_symbol,
                percentage: a.percentage,
                algo_type: a.algo_type,
                entry_speed: a.entry_speed,
                entry_lookback: a.entry_lookback,
                exit_lookback: a.exit_lookback,
                count: a.count,
                avg_duration: a.avg_duration,
                winning_percentage: a.winning_percentage,
                max_drawdown: a.max_drawdown,
                cagr: a.cagr,
                net_max_drawdown: a.max_drawdown * a.percentage / 100,
                net_cagr: a.cagr * a.percentage / 100,
                net_ev: a.avg_profit * a.percentage / 100,
                total_profit: a.avg_profit * a.count,
                net_rr: a.avg_profit * a.count / a.max_drawdown,
                avg_profit: a.avg_profit,
                avg_win: a.avg_win,
                avg_loss: a.avg_loss,
                max_win: a.max_win,
                max_loss: a.max_loss,
                max_correlation: maxCorrelation ? maxCorrelation.sum : null,
                max_correlation_algo_display_name: maxCorrelation ? maxCorrelation.display_name : null,
                max_correlation_algoId: maxCorrelation ? maxCorrelation.algoId : null,
                expectancy: 1 + ((a.winning_percentage / 100 * winLossRatio) - (1 - a.winning_percentage / 100)),
                robustness_score: a.robustness_score,
                live_vs_backtest: a.live_vs_backtest
            }
        })
    }

    function editBoxFormatter(cell, row) {
        return (
            <>
                {props.allowEdit() ? <><Link className="plus-minus-button" onClick={() => onAllocationIncremented(row.id, -1)}>⬇️</Link>&nbsp;&nbsp;</> : null}
                <input onFocus={() => setFocusAlgoId(row.id)} onBlur={() => { setFocusAlgoId(null) }} type="text" inputMode="numeric" size="4" id={row.idalgos} name={row.id} value={cell} onKeyDown={(event) => handleKeyDown(event, cell)} onChange={(e) => onAllocationUpdated(row.id, e)}></input>
                &nbsp; {props.allowEdit() ? <Link className="plus-minus-button" onClick={() => onAllocationIncremented(row.id, 1)}>⬆️</Link> : null}
            </>
        )
    }

    function handleKeyDown(event, value) {
        console.log("Key press", event.key);
        console.log("Focus algo", focusAlgoId);
        if (event.key === 'ArrowUp') {
            if (focusAlgoId != null) {
                onAllocationIncremented(focusAlgoId, 1);
            }
        } else if (event.key === 'ArrowDown') {
            if (focusAlgoId != null) {
                onAllocationIncremented(focusAlgoId, -1);
            }
        } else if (Number.isInteger(value)) {
            onAllocationUpdatedSet(focusAlgoId, Number(value))
        }
    }

    const onAddAlgoToPlan = async (algoId) => {
        console.log("onAddAlgoToPlan", algoId);

        await (await fetch(`/api/addAlgoToPlan?planId=` + props.planId + `&algoId=` + algoId)).json();

        const algo = await (await fetch(`/api/algo?algoId=` + algoId)).json();
        let algoInfo = algo.algo[0];
        console.log("adding", algo);
        let tempData = JSON.parse(JSON.stringify(data));
        let stats = algo.stats.find(stats => stats.years === statYears)
        tempData.push({
            idalgos: algoId,
            algoId: algoId,
            display_name: algoInfo.display_name,
            name: algoInfo.name,
            symbol: algoInfo.symbol,
            short: algoInfo.short,
            trade_symbol: algoInfo.trade_symbol,
            percentage: 0,
            algo_type: algoInfo.algo_type,
            entry_speed: algoInfo.entry_speed,
            entry_lookback: algoInfo.entry_lookback,
            exit_lookback: algoInfo.exit_lookback,
            count: stats ? stats.count : 0,
            avg_duration: stats ? stats.avg_duration : 0,
            winning_percentage: stats ? stats.winning_percentage : 0,
            max_drawdown: stats ? stats.max_drawdown : 0,
            cagr: stats ? stats.cagr : 0,
            net_max_drawdown: 0,
            avg_profit: stats ? stats.avg_profit : 0,
            avg_win: stats ? stats.avg_win : 0,
            avg_loss: stats ? stats.avg_loss : 0,
            max_win: stats ? stats.max_win : 0,
            max_loss: stats ? stats.max_loss : 0,
            robustness_score: algoInfo.robustness_score,
            live_vs_backtest: algoInfo.live_vs_backtest
        })
        setData(tempData);
    }

    async function onAlgoAdded(algoId) {
        console.log("onAlgoAdded", algoId);
        if (props.allowEdit()) {
            await onAddAlgoToPlan(algoId);
        } else {
            setResult({ error: "Not allowed" })
        }
    }

    function addRemoveFormatter(cell, row) {
        return (
            <>
                {props.allowEdit() ?
                    <>

                        <button class="btn btn-secondary" onClick={() => handleSoftRemove(row.id)} type="button">❌</button>
                        &nbsp;<button class="btn btn-secondary" onClick={() => handleSwap(row.id)} type="button">🔄</button>
                    </>
                    : null}
            </>
        )
    }

    function algoLinkFormatter(cell, row) {
        let algoid = row.id;
        return (
            <div className='sameline'>
                <AlgoDrawer link={cell} planId={props.planId} accountId={props.accountId} userId={props.userId} mobileView={props.mobileView} algoId={algoid} role={props.role} />
                &nbsp;<Tooltip text={"🔍"} json={row} position={"right center"} />
            </div>
        )
    }

    function maxCorrelationFormmatter(cell, row) {
        return (
            <div>
                <AlgoDrawer link={row.max_correlation_algo_display_name} planId={props.planId} accountId={props.accountId} userId={props.userId} mobileView={props.mobileView} algoId={row.max_correlation_algoId} role={props.role} />
            </div>
        )
    }

    async function runBacktest() {
        if (!isTestRunning) {
            let fullStartDate = new Date(2014, 0, 1)
            let fullEndDate = new Date()
            let startAmount = 100000;
            let allocations = {};
            data.forEach(allocation => {
                if (allocation.percentage && Number(allocation.percentage)) {
                    allocations[Number(allocation.idalgos)] = Number(allocation.percentage);
                }
            })
            console.log("allocations", allocations);
            const backtestTradesUrl = `/api/backtestRunComplete?planId=` + props.planId +
                "&startDate=" + fullStartDate.toLocaleDateString() +
                "&endDate=" + fullEndDate.toLocaleDateString() +
                "&startAmount=" + startAmount +
                "&activeTradesOnly=0" +
                "&liveTradesOnly=0" +
                "&deleteResults=0" +
                "&allocationStrategyId=4" +
                "&isOfficial=0";

            console.log("url", backtestTradesUrl);

            setIsTestRunning(true);
            setLastBacktestResults(JSON.parse(JSON.stringify(backtestResults)));
            setBacktestResults(null);

            let text = await fetch(backtestTradesUrl, { method: 'POST', body: JSON.stringify(allocations) });
            console.log("response", text);
            let results = await text.json();
            console.log("results", results);

            setBacktestResults(results);
            setIsTestRunning(false);
        }
    }

    function getStats() {
        let results = { cagr: 0.0, max_drawdown: 0.0, total_allocations: 0, total_short: 0.0, total_long: 0.0 };

        getAlgos().forEach(a => {
            results.cagr += a.net_cagr
            results.max_drawdown += a.net_max_drawdown
            results.total_allocations += a.percentage
            results.total_long += a.short ? 0 : a.percentage
            results.total_short += a.short ? a.percentage : 0
        })

        return [results];
    }

    return (
        <>
            <ActionResult result={result} />

            <div className='sameline'>
                Stats for Trades:
                <Dropdown width="150" onSelect={(e) => setStatYears(e)}>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        {statYears} Years
                    </Dropdown.Toggle>

                    <Dropdown.Menu  >
                        <Dropdown.Item as='a' eventKey={0} value={0}>Live</Dropdown.Item>
                        <Dropdown.Item as='g' eventKey={1} value={1}>1 Year</Dropdown.Item>
                        <Dropdown.Item as='b' eventKey={2} value={2}>2 Years</Dropdown.Item>
                        <Dropdown.Item as='e' eventKey={3} value={3}>3 Years</Dropdown.Item>
                        <Dropdown.Item as='c' eventKey={5} value={5}>5 Years</Dropdown.Item>
                        <Dropdown.Item as='d' eventKey={10} value={10}>10 Years</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {props.allowEdit() && (
                <>
                    <div className="card" style={{ padding: "10px", margin: "10px 0" }}>
                        <h5>Batch Update Allocations</h5>
                        <div className="sameline">
                            <div style={{ marginRight: "10px" }}>
                                <label>Trade Symbol</label>
                                <Dropdown onSelect={(e) => setBatchUpdateCriteria({ ...batchUpdateCriteria, trade_symbol: e })}>
                                    <Dropdown.Toggle variant="secondary" size="sm">
                                        {batchUpdateCriteria.trade_symbol}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {['All', ...new Set(data.map(a => a.trade_symbol))].map(s => <Dropdown.Item key={s} eventKey={s}>{s}</Dropdown.Item>)}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div style={{ marginRight: "10px" }}>
                                <label>Algo Type</label>
                                <Dropdown onSelect={(e) => setBatchUpdateCriteria({ ...batchUpdateCriteria, algo_type: e })}>
                                    <Dropdown.Toggle variant="secondary" size="sm">
                                        {batchUpdateCriteria.algo_type}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {['All', ...new Set(data.map(a => a.algo_type))].map(t => <Dropdown.Item key={t} eventKey={t}>{t}</Dropdown.Item>)}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div style={{ marginRight: "10px" }}>
                                <label>Entry Speed</label>
                                <Dropdown onSelect={(e) => setBatchUpdateCriteria({ ...batchUpdateCriteria, entry_speed: e })}>
                                    <Dropdown.Toggle variant="secondary" size="sm">
                                        {batchUpdateCriteria.entry_speed}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {['All', ...new Set(data.map(a => a.entry_speed))].map(s => <Dropdown.Item key={s} eventKey={s}>{s}</Dropdown.Item>)}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div style={{ marginRight: "10px" }}>
                                <label>New Allocation %</label>
                                <input type="number" style={{ width: "80px" }} value={batchUpdateValue} onChange={(e) => setBatchUpdateValue(e.target.value)} placeholder="%" />
                            </div>
                            <button className="btn btn-primary" onClick={handleBatchUpdate}>Apply</button>

                            <div style={{ marginLeft: "20px", paddingLeft: "20px", borderLeft: "1px solid #ccc" }}>
                                <label className="btn btn-secondary" style={{ margin: 0, cursor: 'pointer' }}>
                                    Import CSV
                                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileUpload} />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ padding: "10px", margin: "10px 0" }}>
                        <h5>Auto Allocate</h5>
                        <div className="sameline" style={{ alignItems: 'center' }}>
                            <div style={{ marginRight: "20px" }}>
                                <label>Total %</label><br />
                                <input type="number" style={{ width: "60px" }} value={totalAutoAllocation} onChange={(e) => setTotalAutoAllocation(e.target.value)} />
                            </div>
                            <div style={{ marginRight: "20px" }}>
                                <label>Min %</label><br />
                                <input type="number" style={{ width: "60px" }} value={minAutoAllocation} onChange={(e) => setMinAutoAllocation(e.target.value)} />
                            </div>
                            <div style={{ marginRight: "20px" }}>
                                <label>Max %</label><br />
                                <input type="number" style={{ width: "60px" }} value={maxAutoAllocation} onChange={(e) => setMaxAutoAllocation(e.target.value)} />
                            </div>
                            <div style={{ marginRight: "20px" }}>
                                <label>Type</label><br />
                                <Dropdown onSelect={(e) => setAutoAllocateLongShort(e)}>
                                    <Dropdown.Toggle variant="secondary" size="sm">
                                        {autoAllocateLongShort}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item eventKey="Both">Both</Dropdown.Item>
                                        <Dropdown.Item eventKey="Long">Long</Dropdown.Item>
                                        <Dropdown.Item eventKey="Short">Short</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                            <div style={{ flexGrow: 1, marginRight: "20px", textAlign: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                    <span>Minimize Drawdown</span>
                                    <span>&nbsp;Maximize CAGR</span>
                                </div>
                                <input type="range" min="0" max="100" value={autoAllocatePreference} onChange={(e) => setAutoAllocatePreference(e.target.value)} style={{ width: "100%" }} />
                            </div>
                            <button className="btn btn-primary" onClick={handleAutoAllocate}>Auto Allocate</button>
                        </div>
                    </div>
                </>
            )}

            <RichTable enableExport={true} data={getStats()} className="table-striped table-hover table-condensed fixed" mappers={[
                { title: 'Net CAGR', field: 'cagr', formatter: tableHelpers.percentChangeFormatterOneDecimal },
                { title: 'Net Max DD', field: 'max_drawdown', formatter: tableHelpers.percentChangeFormatterOneDecimal },
                { title: 'Total Allocations', field: 'total_allocations', formatter: tableHelpers.intFormatter },
                { title: 'Total Long', field: 'total_long', formatter: tableHelpers.intFormatter },
                { title: 'Total Short', field: 'total_short', formatter: tableHelpers.intFormatter },
            ]} />

            <RichTable enableExport={true} data={getAlgos()} className="table-striped table-hover table-condensed fixed" mappers={[
                { title: 'Id', field: 'id', formatter: tableHelpers.intFormatter, sorters: true, width: '40px', hidden: props.mobileView },
                { title: 'Algo', field: 'display_name', formatter: algoLinkFormatter, sorters: true, filter: 'text' },
                { title: 'Symbol', field: 'symbol', sorters: true, hidden: props.mobileView, filter: 'text' },
                { title: 'Trade Sym', field: 'trade_symbol', sorters: true, filter: 'text' },
                { title: 'Allocation', field: 'percentage', formatter: editBoxFormatter, sorters: true, width: '120px' },
                { title: 'Type', field: 'algo_type', sorters: true, filter: 'choice', hidden: props.mobileView },
                { title: 'Entry Speed', field: 'entry_lookback', sorters: true, filter: 'choice', hidden: props.mobileView, formatter: tableHelpers.intFormatter },
                { title: 'Robustness', field: 'robustness_score', formatter: tableHelpers.decimalFormatter, sorters: true, hidden: props.mobileView },
                { title: 'Days', field: 'avg_duration', formatter: tableHelpers.intFormatter, sorters: true, width: '60px', hidden: props.mobileView },
                { title: 'CAGR', field: 'cagr', formatter: tableHelpers.percentFormatter, sorters: true, width: '70px', hidden: props.mobileView },
                { title: 'Max DD', field: 'max_drawdown', formatter: tableHelpers.percentFormatter, sorters: true, width: '70px', hidden: props.mobileView },
                { title: 'Net CAGR', field: 'net_cagr', formatter: tableHelpers.percentChangeFormatterOneDecimal, sorters: true, width: '70px', hidden: props.mobileView },
                { title: 'Net Max DD', field: 'net_max_drawdown', formatter: tableHelpers.percentFormatter, sorters: true, width: '70px', hidden: props.mobileView },
                { title: 'Max Correlation', field: 'max_correlation', formatter: intFormatter, sorters: true, width: '70px', hidden: props.mobileView },
                { title: 'Expectancy', field: 'expectancy', formatter: decimalFormatter, sorters: true, width: '70px', hidden: props.mobileView },
                { title: 'Algo', field: 'max_correlation_algo_display_name', formatter: maxCorrelationFormmatter, width: '70px', hidden: props.mobileView },
                { title: 'P/L', field: 'avg_profit', formatter: tableHelpers.percentFormatter, sorters: true, width: '70px', hidden: props.mobileView },
                { title: '#', field: 'count', formatter: tableHelpers.intFormatter, sorters: true, width: '50px', hidden: props.mobileView },
                { title: 'Manage', field: 'id', formatter: addRemoveFormatter, sorters: true },
            ]} />

            <div className='addalgo-button'>
                <Link onClick={() => setHideAlgos(!hideAlgos)}>
                    <AddCircleIcon sx={{ fontSize: 40 }} />
                </Link>
            </div>

            <div className='runtest-button'>
                <Tooltip textClass="big-link" onOpen={() => runBacktest()} triggerJsx={<QueryStatsIcon sx={{ fontSize: 40 }} />} text="Run test..." position="bottom left" width="800px" >
                    <table width="800px">
                        <tr>
                            <th>Latest Run</th>
                            <th width="50%">Previous Run</th>
                        </tr>
                        <tr>
                            <td>
                                {backtestResults ?
                                    <PlanScorecard backtestResults={backtestResults}></PlanScorecard>
                                    :
                                    <CircularProgress />
                                }
                            </td>
                            <td>
                                {lastBacktestResults ?
                                    <PlanScorecard backtestResults={lastBacktestResults}></PlanScorecard>
                                    :
                                    <>None</>
                                }
                            </td>
                        </tr>
                    </table>
                </Tooltip>
            </div>

            {unsavedChanges && props.allowEdit() ?
                <div className='saveAllocations-button'>
                    <TwoStepConfirmationButton id={0} text={"💾 Save changes to floppy (" + unsavedChanges + ")"} onConfirmed={updateAllAllocations}
                        triggerJsx={<>
                            <SaveIcon sx={{ fontSize: 40 }} />
                            <br></br>
                            SAVE CHANGES
                        </>
                        } />
                </div>
                : null}

            <Drawer open={!hideAlgos} onClose={() => setHideAlgos(true)}
                PaperProps={{
                    sx: { width: "90%" },
                }}
            >
                <div style={{ padding: "20px" }}>
                    <div>
                        <h2>All Algos</h2>
                        <AlgoStats role={props.role} userId={props.userId} showAll={true} defaultYears={5} planId={props.planId} mobileView={props.mobileView} onAlgoAction={props.allowEdit() ? onAlgoAdded : null} />
                    </div>

                    <div className='addalgo-button'>
                        <Link onClick={() => setHideAlgos(!hideAlgos)}>
                            <CheckCircleIcon sx={{ fontSize: 40 }} />
                        </Link>
                    </div>
                </div>

            </Drawer>
        </>
    );
}

export default Algos;